import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import type { AccessRicercaSyslog } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime, timeAgo } from '../common'
import { DataTable } from '../DataTable'

/**
 * «Syslog delle sonde» (CENSO ACCESS, delivery 6): the probes that receive
 * the syslog of the network devices, firewalls, Wi‑Fi controllers and RADIUS
 * servers, how much raw they keep (closed segments, their re-reading) and the
 * search in the raw: the probe looks in its segments and sends back only the
 * lines found. Every search is in the register of the views, with what was
 * asked.
 */

function dimensione(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} kB`
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function adessoMeno(ore: number): string {
  const d = new Date(Date.now() - ore * 3600_000)
  d.setSeconds(0, 0)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

export function VistaSyslog() {
  const dati = useAsync(() => api.accessSyslog(), [])
  const sonde = dati.data?.probes ?? []
  const [sonda, setSonda] = useState('')
  const [da, setDa] = useState(adessoMeno(24))
  const [a, setA] = useState(adessoMeno(0))
  const [testo, setTesto] = useState('')
  const [apparato, setApparato] = useState('')
  const [ricerca, setRicerca] = useState<AccessRicercaSyslog | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  useEffect(() => {
    if (!sonda && sonde.length) setSonda(sonde[0].probe_id)
  }, [sonde, sonda])

  useEffect(() => {
    if (!ricerca || ricerca.status !== 'pending') return
    const timer = setInterval(() => {
      api.accessSyslogSearchResult(ricerca.id).then(setRicerca).catch(setErrore)
    }, 3000)
    return () => clearInterval(timer)
  }, [ricerca])

  async function cerca(e: React.FormEvent) {
    e.preventDefault()
    setErrore(null)
    try {
      setRicerca(await api.accessSyslogSearch({
        probe_id: sonda, from: new Date(da).toISOString(), to: new Date(a).toISOString(),
        contains: testo.trim() || undefined, device: apparato.trim() || undefined, limit: 2000,
      }))
    } catch (err) {
      setErrore(err)
    }
  }

  return (
    <Card title={t('Syslog delle sonde')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Una sonda con la funzione «Syslog per CENSO ACCESS» riceve il syslog di apparati di rete, firewall, controller Wi‑Fi e server RADIUS e ne conserva tutto il grezzo, in segmenti orari compressi, in sola lettura e immutabili, con l’impronta registrata qui e riletta ogni giorno. Al registro arrivano solo gli accessi riconosciuti (Cisco, Huawei, FortiGate, Palo Alto, Juniper, Aruba, WatchGuard, FreeRADIUS, Cisco ISE). Il resto si cerca qui: la sonda guarda nei suoi segmenti e manda solo le righe trovate.')}
      </p>
      <ErrorBanner error={dati.error ?? errore} />
      {dati.loading && !dati.data ? <Spinner /> : sonde.length === 0 ? (
        <EmptyState title={t('Nessuna sonda riceve il syslog.')} hint={t('Si abilita la funzione «Syslog per CENSO ACCESS» nella scheda della sonda, poi si puntano gli apparati alla sonda (porta 514, UDP o TCP).')} />
      ) : (
        <>
          <DataTable rows={sonde} rowKey={(s) => s.probe_id} paginazione={false} strumenti={false} columns={[
            { key: 'probe_name', header: t('Sonda'), value: (s) => s.probe_name, render: (s) => <strong>{s.probe_name}</strong> },
            { key: 'live', header: t('Ricezione'), value: (s) => String(s.live?.received ?? ''), render: (s) => (
              s.live?.port ? <>{t('porta {p}', { p: s.live.port })} · {t('{n} righe', { n: s.live.received ?? 0 })}{(s.live.dropped ?? 0) > 0 && <> · <span className="eagle-motivo">{t('{n} scartate', { n: s.live.dropped })}</span></>}</>
                : <span className="muted">{s.live?.error ?? t('non in ascolto')}</span>
            ) },
            { key: 'segments', header: t('Grezzo conservato'), type: 'number', value: (s) => s.bytes, render: (s) => (
              <>{t('{n} segmenti', { n: s.segments })} · {dimensione(s.bytes)}{s.first_at && <><br /><small className="muted">{t('dal {quando}', { quando: formatDateTime(s.first_at) })}</small></>}</>
            ) },
            { key: 'copies', header: t('Copie'), value: (s) => (s.copies ?? []).map((c) => c.probe_name).join(' '), render: (s) => (
              (s.copies ?? []).length === 0 ? <span className="muted">—</span>
                : <>{(s.copies ?? []).map((c) => <div key={c.probe_id}>{c.probe_name}: {t('{n} segmenti', { n: c.segments })}{c.problems > 0 && <> <span className="badge badge--error">{t('{n} segmenti con problemi', { n: c.problems })}</span></>}</div>)}</>
            ) },
            { key: 'checked_at', header: t('Ultima rilettura'), type: 'date', value: (s) => s.checked_at ?? '', render: (s) => (
              <>{s.checked_at ? <span title={formatDateTime(s.checked_at)}>{timeAgo(s.checked_at)}</span> : '—'}{s.problems > 0 && <> <span className="badge badge--error">{t('{n} segmenti con problemi', { n: s.problems })}</span></>}</>
            ) },
          ]} />
          <h4 className="form__sezione">{t('Cerca nel grezzo')}</h4>
          <form className="form-grid" onSubmit={(e) => void cerca(e)}>
            <label className="field span-2"><span className="field__label">{t('Sonda')}</span>
              <select value={sonda} onChange={(e) => setSonda(e.target.value)}>
                {sonde.map((s) => <option key={s.probe_id} value={s.probe_id}>{s.probe_name}</option>)}
              </select></label>
            <label className="field span-2"><span className="field__label">{t('Da')}</span>
              <input type="datetime-local" value={da} onChange={(e) => setDa(e.target.value)} /></label>
            <label className="field span-2"><span className="field__label">{t('A')}</span>
              <input type="datetime-local" value={a} onChange={(e) => setA(e.target.value)} /></label>
            <label className="field span-3"><span className="field__label">{t('Contiene')}</span>
              <input type="text" value={testo} onChange={(e) => setTesto(e.target.value)} placeholder={t('es. admin, LOGIN_FAILED, 203.0.113.9')} /></label>
            <label className="field span-2"><span className="field__label">{t('Apparato (indirizzo)')}</span>
              <input type="text" value={apparato} onChange={(e) => setApparato(e.target.value)} placeholder="10.0.0.1" /></label>
            <div className="span-1" style={{ alignSelf: 'end' }}><button type="submit" disabled={!sonda}>{t('Cerca')}</button></div>
          </form>
          {ricerca && (
            <div style={{ marginTop: 10 }}>
              {ricerca.status === 'pending' ? <p className="muted"><Spinner /> {t('La sonda sta cercando: risponde al prossimo contatto (entro un minuto).')}</p>
                : ricerca.status === 'expired' ? <p className="eagle-motivo">{t('La sonda non ha risposto entro mezz’ora: è online?')}</p>
                  : ricerca.lines.length === 0 ? <EmptyState title={t('Nessuna riga trovata.')} /> : (
                    <>
                      {ricerca.truncated && <p className="field__hint">{t('Mostrate le prime righe: restringi il periodo o il testo per vederle tutte.')}</p>}
                      <DataTable rows={ricerca.lines.map((r, i) => ({ ...r, i }))} rowKey={(r) => String(r.i)} conteggio={{ nome: t('righe') }} paginazione={{ predefinita: 50 }} columns={[
                        { key: 'at', header: t('Quando'), type: 'date', value: (r) => r.at, render: (r) => formatDateTime(r.at), nowrap: true },
                        { key: 'device', header: t('Apparato'), mono: true, filter: 'select', value: (r) => r.device },
                        { key: 'text', header: t('Riga'), mono: true, value: (r) => r.text, render: (r) => <small style={{ wordBreak: 'break-all' }}>{r.text}</small> },
                      ]} />
                    </>
                  )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}
